// Boton «Artículo» junto a «Texto rápido» al escribir una respuesta o un correo: busca en la base de conocimiento
// (buscador del servidor, con respaldo en el navegador) e inserta el articulo: completo si es corto, si no su
// resumen y el enlace. Los accesos de la boveda nunca se insertan.
import { useQuery } from '@tanstack/react-query';
import { useEffect, useMemo, useState } from 'react';
import { createPortal } from 'react-dom';

import { type Articulo, buscarEnServidor, type Resultado, useArticulos } from '../api/conocimiento';
import { useDebounced } from '../hooks';
import { buscarArticulos, fragmentoLocal, masConsultados, textoParaRespuesta } from '../lib/conocimiento';
import { Icon } from '../lib/icons';
import { Fragmento } from './Fragmento';
import { toast } from './toasts';

export function InsertarArticulo({ onElegir }: { onElegir: (texto: string) => void }) {
  const [ancla, setAncla] = useState<DOMRect | null>(null);
  return (
    <>
      <button type="button" className="btn sm ghost" onClick={(e) => setAncla(e.currentTarget.getBoundingClientRect())} aria-haspopup="dialog" title="Insertar un artículo de Conocimiento">
        <Icon name="book" /> <span className="lbl-m">Artículo</span>
      </button>
      {ancla ? createPortal(<Selector ancla={ancla} onCerrar={() => setAncla(null)} onElegir={onElegir} />, document.body) : null}
    </>
  );
}

function Selector({ ancla, onCerrar, onElegir }: { ancla: DOMRect; onCerrar: () => void; onElegir: (t: string) => void }) {
  const r = useArticulos();
  const [q, setQ] = useState('');
  const dq = useDebounced(q, 220);
  const servidor = useQuery({ queryKey: ['kb-buscar', dq.trim()], queryFn: ({ signal }) => buscarEnServidor(dq, '', signal), enabled: dq.trim().length >= 2, staleTime: 30_000 });
  const porId = useMemo(() => new Map((r.data ?? []).map((a) => [a.id, a])), [r.data]);
  const lista: Resultado[] = useMemo(() => {
    const t = dq.trim();
    if (t.length < 2) return masConsultados(r.data ?? [], 8).map((a) => ({ id: a.id, titulo: a.titulo, categoria: a.categoria, resumen: a.resumen, fragmento: null, vistas: a.vistas }));
    if (servidor.data) return servidor.data.articulos;
    if (servidor.isFetching) return [];
    return buscarArticulos(r.data ?? [], t)
      .slice(0, 20)
      .map((a) => ({ id: a.id, titulo: a.titulo, categoria: a.categoria, resumen: a.resumen, fragmento: fragmentoLocal(a.cuerpo, t), vistas: a.vistas }));
  }, [dq, servidor.data, servidor.isFetching, r.data]);
  const [i, setI] = useState(0);
  useEffect(() => setI(0), [lista.length, dq]);

  const elegir = (x: Resultado) => {
    const a: Articulo | undefined = porId.get(x.id);
    if (!a) return toast('Ese artículo aún se está cargando. Intenta de nuevo en un momento.', 'error');
    onElegir(textoParaRespuesta(a, location.origin));
    onCerrar();
  };
  const ancho = Math.min(420, window.innerWidth - 16);
  return (
    <>
      <div style={{ position: 'fixed', inset: 0, zIndex: 89 }} onClick={onCerrar} />
      <div
        className="menu tr-menu kb-sel"
        role="dialog"
        aria-label="Insertar artículo"
        style={{ bottom: Math.max(8, window.innerHeight - ancla.top + 6), left: Math.max(8, Math.min(ancla.left, window.innerWidth - ancho - 8)), width: ancho }}
        onKeyDown={(e) => {
          if (e.key === 'Escape') onCerrar();
          else if (e.key === 'ArrowDown') (e.preventDefault(), setI((k) => Math.min(lista.length - 1, k + 1)));
          else if (e.key === 'ArrowUp') (e.preventDefault(), setI((k) => Math.max(0, k - 1)));
          else if (e.key === 'Enter' && lista[i]) (e.preventDefault(), elegir(lista[i]));
        }}
      >
        <input autoFocus className="inp" value={q} onChange={(e) => setQ(e.target.value)} placeholder="Buscar en Conocimiento…" aria-label="Buscar artículo" />
        <div className="tr-lista" role="listbox" aria-label="Artículos">
          {!dq.trim() && lista.length ? <p className="kb-sel-g">Lo más consultado</p> : null}
          {lista.length ? (
            lista.map((x, k) => (
              <button key={x.id} role="option" aria-selected={k === i} onMouseMove={() => setI(k)} onClick={() => elegir(x)}>
                <b>{x.titulo}</b>
                <span>{x.fragmento ? <Fragmento texto={x.fragmento} /> : x.resumen || x.categoria}</span>
              </button>
            ))
          ) : (
            <p className="note" style={{ padding: 10 }}>
              {r.isLoading || servidor.isFetching ? 'Buscando…' : dq.trim().length >= 2 ? 'Nada con esa búsqueda.' : 'Escribe para buscar un artículo.'}
            </p>
          )}
        </div>
        <p className="kb-sel-pie">Se inserta completo si es corto; si no, el resumen y el enlace.</p>
      </div>
    </>
  );
}
